
import React, { useMemo, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCheckout } from "@/hooks/useCheckout";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/contexts/CartContext";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { CartItem } from "@/contexts/CartContext";

export interface OrderSummaryProps {
  onCheckout?: () => void;
  disableCheckout?: boolean;
  total?: number;
  onClearCart?: () => void;
  isSubmitting?: boolean;
  items?: CartItem[];
  customerInfo?: { email: string; name: string };
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  onCheckout,
  disableCheckout = false,
  total,
  onClearCart,
  isSubmitting,
  items,
  customerInfo
}) => {
  const { toast } = useToast();
  const { items: cartItems, getTotal } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Import the checkout hook
  const { handleCheckout } = useCheckout();

  // Use props values if provided, otherwise use values from cart context
  const displayItems = items || cartItems;
  const displayTotal = total !== undefined ? total : getTotal();
  
  // Calculate discount amount for promo codes
  const calculatePromoDiscount = (code: string, items: CartItem[]): number => {
    const upperCode = code.toUpperCase();
    
    // Check if it's a TRIBAL code (TRIBAL10, TRIBAL20, TRIBAL30, etc.)
    const tribalMatch = upperCode.match(/^TRIBAL(\d+)$/);
    if (!tribalMatch) return 0;
    
    const discountAmount = parseInt(tribalMatch[1]);
    
    // Find RV site booking
    const rvItem = items.find(item => item.siteId);
    if (!rvItem) return 0;
    
    // Return fixed discount amount (not per night)
    return discountAmount;
  };
  
  // Apply promo discount
  const promoDiscount = promoApplied ? calculatePromoDiscount(promoCode, displayItems) : 0;
  const discountedSubtotal = Math.max(0, displayTotal - promoDiscount);
  
  // Calculate processing fee based on discounted subtotal
  const getProcessingFee = (amount: number): number => {
    if (amount <= 49) return 3;
    else if (amount <= 110) return 5;
    else if (amount <= 187) return 7;
    else if (amount <= 299) return 10;
    else if (amount <= 440) return 15;
    else if (amount <= 600) return 20;
    else if (amount <= 800) return 30;
    else return 35;
  };

  // Calculate processing fee on discounted subtotal
  const processingFee = useMemo(() => {
    return getProcessingFee(discountedSubtotal);
  }, [discountedSubtotal]);

  // Calculate total price including processing fee
  const totalPrice = useMemo(() => {
    return discountedSubtotal + processingFee;
  }, [discountedSubtotal, processingFee]);

  // Handle promo code application
  const handleApplyPromo = () => {
    const upperCode = promoCode.toUpperCase();
    const tribalMatch = upperCode.match(/^TRIBAL(\d+)$/);
    
    if (tribalMatch) {
      const discount = calculatePromoDiscount(upperCode, displayItems);
      if (discount > 0) {
        setPromoApplied(true);
        setDiscountAmount(discount);
        toast({
          title: "Promo Code Applied",
          description: `Tribal member discount: $${discount} off`,
        });
      } else {
        toast({
          title: "Invalid for this order",
          description: `${upperCode} discount applies only to RV site bookings.`,
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Invalid Promo Code",
        description: "Please check your promo code and try again.",
        variant: "destructive",
      });
    }
  };

  // Handle promo code removal
  const handleRemovePromo = () => {
    setPromoApplied(false);
    setDiscountAmount(0);
    setPromoCode("");
    toast({
      title: "Promo Code Removed",
      description: "Discount has been removed from your order.",
    });
  };

  // Validate customer information
  const isCustomerInfoValid = useMemo(() => {
    if (!customerInfo) return false;
    return customerInfo.email && customerInfo.email.trim() && 
           customerInfo.name && customerInfo.name.trim() &&
           customerInfo.email !== "guest@example.com" &&
           customerInfo.name !== "Guest User";
  }, [customerInfo]);

  // Handle checkout process
  const handleCheckoutProcess = async () => {
    if (displayItems.length === 0) {
      toast({
        title: "Empty Cart",
        description: "Please add items to your cart before checking out.",
        variant: "destructive",
      });
      return;
    }

    // Validate customer info
    if (!isCustomerInfoValid) {
      toast({
        title: "Missing Customer Information",
        description: "Please ensure customer name and email are provided for all items.",
        variant: "destructive",
      });
      return;
    }

    console.log("[OrderSummary] Starting checkout process with customer info:", customerInfo);
    setIsLoading(true);
    
    if (onCheckout) {
      onCheckout();
      setIsLoading(false);
      return;
    }

    try {
      // Prepare cart items for checkout with complete camping data
      const checkoutItems = displayItems.map(item => {
        // Extract camping data if present
        const includesCamping = item.includesCamping || false;
        // Use the dedicated camping date fields with fallback to the main date fields
        const campingStartDate = includesCamping ? (item.campingStartDate || item.startDate) : null;
        const campingEndDate = includesCamping ? (item.campingEndDate || item.endDate) : null;
        const campingPrice = includesCamping ? Number(item.campingTotal || item.campingPrice || 0) : 0;
        const basePrice = Number(item.basePrice || 0);
        const permitPrice = Number(item.permitPrice || item.basePrice || 0);
        const totalPrice = Number(item.totalPrice || (basePrice + campingPrice));
        
        // Log checkout item data for debugging
        console.log('[OrderSummary] Preparing checkout item with explicit price conversions:', {
          permitName: item.permitName,
          totalPrice: `${totalPrice} (${typeof totalPrice})`,
          basePrice: `${basePrice} (${typeof basePrice})`,
          permitPrice: `${permitPrice} (${typeof permitPrice})`,
          campingPrice: `${campingPrice} (${typeof campingPrice})`,
          includesCamping,
          campingStartDate,
          campingEndDate,
          customerEmail: item.customerEmail,
          customerName: item.customerName
        });

        // Return a new object with all required fields explicitly declared
        return {
          ...item,
          permitType: item.type,
          basePrice: basePrice,
          permitPrice: permitPrice,
          totalPrice: totalPrice,
          campingPrice: campingPrice,
          campingTotal: campingPrice,
          includesCamping: includesCamping,
          campingStartDate: campingStartDate,
          campingEndDate: campingEndDate,
          // Ensure customer info is in the item
          customerEmail: customerInfo?.email || item.customerEmail,
          customerName: customerInfo?.name || item.customerName
        };
      });

      const customerData = customerInfo || {
        email: "",
        name: "",
      };

      console.log("[OrderSummary] Final customer data being sent:", customerData);

      // Create checkout session using the handleCheckout from useCheckout
      await handleCheckout(checkoutItems, customerData, undefined, promoApplied ? promoCode : undefined);
      
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Checkout Error",
        description: "An error occurred during checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <h2 className="text-lg font-semibold mb-4">Order Summary</h2>

      <div className="space-y-2">
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>${displayTotal.toFixed(2)}</span>
        </div>

        {/* Show discount if applied */}
        {promoApplied && promoDiscount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Discount ({promoCode.toUpperCase()})</span>
            <span>-${promoDiscount.toFixed(2)}</span>
          </div>
        )}

        <div className="flex justify-between">
          <span>Processing Fee</span>
          <span>${processingFee.toFixed(2)}</span>
        </div>

        <Separator className="my-2" />

        <div className="flex justify-between font-bold text-lg">
          <span>Total</span>
          <span>${totalPrice.toFixed(2)}</span>
        </div>
      </div>

      {/* Promo Code Section */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h3 className="text-sm font-medium mb-2">Promo Code</h3>
        {!promoApplied ? (
          <div className="flex gap-2">
            <Input
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={handleApplyPromo}
              disabled={!promoCode.trim()}
            >
              Apply
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded p-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-green-800 font-medium">{promoCode.toUpperCase()} Applied</span>
              <span className="text-sm text-green-600">-${promoDiscount.toFixed(2)}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemovePromo}
              className="text-green-800 hover:text-green-900"
            >
              Remove
            </Button>
          </div>
        )}
      </div>

      {/* Show customer info validation warning */}
      {!isCustomerInfoValid && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          Customer information is required for checkout. Please ensure all items have valid customer details.
        </div>
      )}

      <Button
        className="w-full mt-4 bg-tribal-primary hover:bg-tribal-primary/90"
        onClick={handleCheckoutProcess}
        disabled={(isLoading || isSubmitting) || disableCheckout || displayItems.length === 0 || !isCustomerInfoValid}
      >
        {(isLoading || isSubmitting) ? <LoadingSpinner /> : "Proceed to Checkout"}
      </Button>
      
      {onClearCart && (
        <Button
          variant="outline" 
          className="w-full mt-2"
          onClick={onClearCart}
          disabled={isLoading || isSubmitting}
        >
          Clear Cart
        </Button>
      )}
    </div>
  );
};

export default OrderSummary;
